import React, { FC, Fragment, useState } from 'react';
import {
    Box,
    IconButton,
    Link,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useQuery } from '@apollo/client';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { productionOverviewSnapshotTemplate } from 'apollo/templates/production-overview';
import { OrderFilter } from 'models/order';
import {
    ProductionOverviewRowKey,
    ProductionOverviewSnapshotDepartment,
    ProductionOverviewSnapshotStep,
} from 'models/production-overview';
import Loader from 'components/shared/loader/loader.component';
import { OverviewCellClickHandler } from './overview-group-row.component';
import { buildWorkflowOrdersUrl } from './production-overview-utils';
import { OVERVIEW_POLL_INTERVAL_MS } from './use-production-overview-data.hook';

type InProductionSectionProps = {
    clinicId?: string;
    onCellClick: OverviewCellClickHandler;
};

type SnapshotFlag = { isLate?: boolean; isPaused?: boolean };

/** The backend's pseudo step for orders outside every workflow step. */
const NO_STEP_ID = 'NO_STEP';

const stepCells = (steps: ProductionOverviewSnapshotStep[], flag: SnapshotFlag) =>
    steps.map((step) => ({ rowKey: ProductionOverviewRowKey.SNAPSHOT_STEP, stepId: step.stepId, ...flag }));

const sumSteps = (steps: ProductionOverviewSnapshotStep[], field: keyof ProductionOverviewSnapshotStep): number =>
    steps.reduce((total, step) => total + Number(step[field] || 0), 0);

type SnapshotCountCellProps = {
    count: number;
    color?: string;
    /** A step's count opens that step's list on the Workflow tab (DD-3880). */
    href?: string;
    onClick?: () => void;
};

const SnapshotCountCell: FC<SnapshotCountCellProps> = ({ count, color, href, onClick }: SnapshotCountCellProps) => {
    const isClickable = count > 0 && Boolean(onClick) && !href;
    const countText = (
        <Typography variant="body2" fontWeight={500} color={color}>
            {count}
        </Typography>
    );

    return (
        <TableCell
            align="center"
            onClick={isClickable ? onClick : undefined}
            sx={{
                cursor: isClickable ? 'pointer' : 'default',
                '&:hover': isClickable ? { textDecoration: 'underline' } : undefined,
            }}
        >
            {count > 0 && href ? (
                <Link component={RouterLink} to={href} color="inherit" underline="hover">
                    {countText}
                </Link>
            ) : undefined}
            {count > 0 && !href ? countText : undefined}
        </TableCell>
    );
};

const InProductionSection: FC<InProductionSectionProps> = ({ clinicId, onCellClick }: InProductionSectionProps) => {
    const { t: translate } = useTranslation(['production-overview', 'orders', 'workflow']);
    const [expandedDepartments, setExpandedDepartments] = useState<Set<string>>(new Set());

    // Always shown since DD-3880, so it loads with the page and refreshes with the calendar.
    // The clinic filter narrows it like every other number (DD-3800).
    const {
        data,
        loading: isLoading,
        error,
    } = useQuery(productionOverviewSnapshotTemplate, {
        variables: { clinicId },
        fetchPolicy: 'cache-and-network',
        pollInterval: OVERVIEW_POLL_INTERVAL_MS,
    });

    const toggleDepartment = (departmentName: string) => {
        const next = new Set(expandedDepartments);
        if (next.has(departmentName)) next.delete(departmentName);
        else next.add(departmentName);
        setExpandedDepartments(next);
    };

    const departments: ProductionOverviewSnapshotDepartment[] = data?.productionOverviewSnapshot || [];
    // The backend groups steps outside any department under null names, and orders outside
    // any step under the pseudo step NO_STEP with an empty name — both get readable labels.
    const departmentViews = departments.map((department) => ({
        key: department.departmentId || 'NO_DEPARTMENT',
        label: department.departmentName || translate('workflow:NO_DEPARTMENT'),
        steps: department.steps.map((step) => ({ ...step, label: step.stepName || translate('workflow:NO_STEP') })),
    }));
    const sectionLabel = translate('production-overview:IN_PRODUCTION_RIGHT_NOW');

    const handleSnapshotClick = (labelParts: string[], steps: ProductionOverviewSnapshotStep[], flag: SnapshotFlag) => {
        onCellClick([sectionLabel, ...labelParts], stepCells(steps, flag));
    };

    // Same link as the Workflow tab's step boxes; "No step" has no id there either.
    const stepListUrl = (step: ProductionOverviewSnapshotStep & { label: string }, extraFilters: OrderFilter = {}) =>
        buildWorkflowOrdersUrl(step.label, step.stepId === NO_STEP_ID ? undefined : step.stepId, {
            ...(clinicId && { clinicId }),
            ...extraFilters,
        });

    const messageRow = (message: React.ReactNode) => (
        <TableRow>
            <TableCell colSpan={4}>{message}</TableCell>
        </TableRow>
    );

    return (
        <TableContainer component={Paper} sx={{ marginTop: 2 }} data-test="production-overview.in-production">
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ minWidth: 220 }}>
                            <Box display="flex" alignItems="center" gap={0.5}>
                                {sectionLabel}
                                <Tooltip title={translate('production-overview:SNAPSHOT_NO_PLAN_TOOLTIP')}>
                                    <InfoOutlinedIcon fontSize="small" color="disabled" />
                                </Tooltip>
                            </Box>
                        </TableCell>
                        <TableCell align="center">{translate('production-overview:SNAPSHOT_COLUMN_COUNT')}</TableCell>
                        <TableCell align="center">{translate('production-overview:SNAPSHOT_COLUMN_LATE')}</TableCell>
                        <TableCell align="center">{translate('production-overview:SNAPSHOT_COLUMN_PAUSED')}</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {isLoading && !data ? messageRow(<Loader />) : undefined}
                    {error
                        ? messageRow(
                              <Typography variant="body2" color="error">
                                  {translate('production-overview:SNAPSHOT_LOAD_ERROR')}
                              </Typography>,
                          )
                        : undefined}
                    {data && !departments.length
                        ? messageRow(
                              <Typography variant="body2" color="text.secondary">
                                  {translate('production-overview:SNAPSHOT_EMPTY')}
                              </Typography>,
                          )
                        : undefined}
                    {departmentViews.map((department) => (
                        <Fragment key={department.key}>
                            <TableRow data-test="production-overview.snapshot-department">
                                <TableCell>
                                    <Box display="flex" alignItems="center">
                                        <IconButton
                                            size="small"
                                            onClick={() => toggleDepartment(department.key)}
                                            aria-label={department.label}
                                        >
                                            {expandedDepartments.has(department.key) ? (
                                                <ExpandLessIcon fontSize="small" />
                                            ) : (
                                                <ExpandMoreIcon fontSize="small" />
                                            )}
                                        </IconButton>
                                        <Typography variant="body2" fontWeight={600}>
                                            {department.label}
                                        </Typography>
                                    </Box>
                                </TableCell>
                                <SnapshotCountCell
                                    count={sumSteps(department.steps, 'orderCount')}
                                    onClick={() => handleSnapshotClick([department.label], department.steps, {})}
                                />
                                <SnapshotCountCell
                                    count={sumSteps(department.steps, 'lateCount')}
                                    color="error.main"
                                    onClick={() =>
                                        handleSnapshotClick(
                                            [department.label, translate('production-overview:LATE')],
                                            department.steps,
                                            { isLate: true },
                                        )
                                    }
                                />
                                <SnapshotCountCell
                                    count={sumSteps(department.steps, 'pausedCount')}
                                    color="warning.main"
                                    onClick={() =>
                                        handleSnapshotClick(
                                            [department.label, translate('production-overview:PAUSED')],
                                            department.steps,
                                            { isPaused: true },
                                        )
                                    }
                                />
                            </TableRow>
                            {expandedDepartments.has(department.key)
                                ? department.steps.map((step) => (
                                      <TableRow key={step.stepId} data-test="production-overview.snapshot-step">
                                          <TableCell sx={{ paddingLeft: 7 }}>
                                              <Typography variant="body2">{step.label}</Typography>
                                          </TableCell>
                                          <SnapshotCountCell count={step.orderCount} href={stepListUrl(step)} />
                                          {/* The Workflow list has no late filter, so late stays a dialog. */}
                                          <SnapshotCountCell
                                              count={step.lateCount}
                                              color="error.main"
                                              onClick={() =>
                                                  handleSnapshotClick(
                                                      [
                                                          department.label,
                                                          step.label,
                                                          translate('production-overview:LATE'),
                                                      ],
                                                      [step],
                                                      { isLate: true },
                                                  )
                                              }
                                          />
                                          <SnapshotCountCell
                                              count={step.pausedCount}
                                              color="warning.main"
                                              href={stepListUrl(step, { isPaused: true })}
                                          />
                                      </TableRow>
                                  ))
                                : undefined}
                        </Fragment>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default InProductionSection;
